import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { PlayCircle, Lock, Clock, ArrowRight } from "lucide-react";

export default function FreeContent() {
  const [lessons, setLessons] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [lessonsRes, progsRes] = await Promise.all([
          fetch(`${API}/programs/free/content`),
          fetch(`${API}/programs`),
        ]);
        if (lessonsRes.ok) setLessons(await lessonsRes.json());
        if (progsRes.ok) setPrograms(await progsRes.json());
      } catch {}
      setLoading(false);
    };
    load();
  }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="free-content-page">
      <Navbar transparent />

      {/* Hero */}
      <section className="pt-24 pb-12 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-[#193B6E] mb-4" style={{ fontFamily: 'Outfit' }}>
            Contenido <span className="text-[#20D3C0]">Gratuito</span>
          </h1>
          <p className="text-slate-500 max-w-2xl mx-auto">
            Explora lecciones gratuitas de nuestros programas de preparacion para bachillerato
          </p>
        </div>
      </section>

      {/* Ad Banner */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 mb-8">
        <div className="ad-placeholder h-24 rounded-xl" data-testid="ad-banner-top">
          <p>Espacio publicitario - Banner 728x90</p>
        </div>
      </div>

      {/* Content */}
      <section className="px-6 md:px-12 lg:px-24 pb-16">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-[#20D3C0] border-t-transparent rounded-full"></div>
            </div>
          ) : lessons.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-400">No hay contenido gratuito disponible por ahora</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {lessons.map((lesson, i) => (
                <React.Fragment key={lesson.lesson_id}>
                  <div className="bg-white rounded-2xl overflow-hidden border border-slate-100 shadow-[0_8px_30px_rgba(25,59,110,0.08)] transition-all hover:shadow-[0_20px_40px_rgba(25,59,110,0.12)] hover:-translate-y-1" data-testid={`free-lesson-${lesson.lesson_id}`}>
                    <div className="aspect-video bg-gradient-to-br from-[#193B6E] to-[#112C54] flex items-center justify-center relative">
                      <PlayCircle className="w-12 h-12 text-white/50" />
                      <span className="absolute top-3 left-3 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                        GRATIS
                      </span>
                    </div>
                    <div className="p-6">
                      <span className="text-xs font-semibold text-[#20D3C0] bg-[#20D3C0]/10 px-2 py-1 rounded-full">
                        {lesson.program_id === 'exacer' ? 'Exacer-COLBACH' : 'Acredita-Bach'}
                      </span>
                      <h3 className="text-lg font-semibold text-[#193B6E] mt-3 mb-2" style={{ fontFamily: 'Outfit' }}>
                        {lesson.title}
                      </h3>
                      <p className="text-sm text-slate-500 mb-4">{lesson.description}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-400 flex items-center gap-1">
                          <Clock className="w-3 h-3" /> {lesson.duration_minutes} min
                        </span>
                        <Link
                          to={`/program/${lesson.program_id}/lesson/${lesson.lesson_id}`}
                          className="text-[#20D3C0] font-medium text-sm flex items-center gap-1 hover:underline"
                        >
                          Ver <ArrowRight className="w-3 h-3" />
                        </Link>
                      </div>
                    </div>
                  </div>
                  {/* Insert ad every 3 items */}
                  {(i + 1) % 3 === 0 && (
                    <div className="md:col-span-2 lg:col-span-3">
                      <div className="ad-placeholder h-20 rounded-xl" data-testid={`ad-inline-${i}`}>
                        <p>Espacio publicitario - Banner 728x90</p>
                      </div>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          )}

          {/* CTA to premium */}
          <div className="mt-16 bg-gradient-to-r from-[#193B6E] to-[#112C54] rounded-2xl p-8 md:p-12 text-center text-white">
            <Lock className="w-10 h-10 mx-auto mb-4 opacity-50" />
            <h2 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Outfit' }}>Desbloquea todo el contenido</h2>
            <p className="text-slate-300 mb-6 max-w-lg mx-auto">
              Accede a todos los videos, material PDF, cuestionarios y clases en vivo sin publicidad
            </p>
            <Link
              to="/payment"
              className="inline-flex items-center gap-2 bg-[#20D3C0] text-[#112C54] hover:bg-[#1AB8A6] font-semibold rounded-full px-8 py-3 transition-all"
              data-testid="free-upgrade-btn"
            >
              Hacerse miembro <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Bottom Ad */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 mb-8">
        <div className="ad-placeholder h-24 rounded-xl" data-testid="ad-banner-bottom">
          <p>Espacio publicitario - Banner 728x90</p>
        </div>
      </div>

      <Footer />
    </div>
  );
}
